import { useEffect, useState } from 'react'

import { fetchUserCallEvents } from '@/actions/userCallEventsActions'
import type { UserCallEvent } from '@/reducers/finances/userCallEventsReducer'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  BOOKING_POLL_INTERVAL_MS,
  BOOKING_POLL_MAX_ATTEMPTS,
} from './constants'
import {
  type AwaitedBooking,
  matchesAwaitedBooking,
} from './liveOnboarding.helpers'

export const useAwaitedBooking = (
  callEvent: UserCallEvent | undefined
): {
  isAwaiting: boolean
  displayedEvent: UserCallEvent | undefined
  awaitBooking: (awaited: AwaitedBooking) => void
} => {
  const dispatch = useAppDispatch()
  const [awaited, setAwaited] = useState<AwaitedBooking | null>(null)
  const arrived = Boolean(
    awaited && callEvent && matchesAwaitedBooking(callEvent, awaited)
  )

  useEffect(() => {
    if (arrived) setAwaited(null)
  }, [arrived])

  useEffect(() => {
    if (!awaited || arrived) return undefined
    let attempts = 0
    const interval = setInterval(() => {
      attempts += 1
      dispatch(fetchUserCallEvents())
      if (attempts < BOOKING_POLL_MAX_ATTEMPTS) return
      clearInterval(interval)
      // An unreported reschedule falls back to whatever booking exists.
      if (!awaited.eventUuid) setAwaited(null)
    }, BOOKING_POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [awaited, arrived, dispatch])

  const hidesUnmatched = Boolean(awaited?.eventUuid) && !arrived
  return {
    isAwaiting: awaited !== null,
    displayedEvent: hidesUnmatched ? undefined : callEvent,
    awaitBooking: setAwaited,
  }
}
